import React, { useEffect } from 'react';
import { format } from 'date-fns';
import { useSystem } from '../../contexts/SystemContext';
import { useAuth } from '../../hooks/useAuth';

export function CostsTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    costData,
    loading,
    error,
    fetchCostData,
    startCostPolling,
    stopCostPolling,
  } = useSystem();

  // Cost data is scoped to this tab: initial fetch + polling while mounted
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCostData();
      startCostPolling();
      return () => stopCostPolling();
    }
  }, [isAuthenticated, authLoading, fetchCostData, startCostPolling, stopCostPolling]);

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button onClick={fetchCostData} className="tc-btn tc-btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const dailyPercentage = costData?.daily?.percentage || 0;
  const monthlyPercentage = costData?.monthly?.percentage || 0;

  return (
    <div className="space-y-6">
      {/* Cost Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Cost Overview</h3>
          {loading && !costData ? (
            <div className="text-center py-4">
              <div className="tc-spinner mb-2"></div>
              <p className="tc-text-secondary text-sm">Loading cost data...</p>
            </div>
          ) : costData ? (
            <div className="space-y-4">
              {/* Daily Spending */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm tc-text-secondary">Daily Spending</span>
                  <span className="text-sm font-medium tc-text-primary">
                    ${costData.daily?.spent?.toFixed(2) || '0.00'} / ${costData.daily?.budget?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      dailyPercentage > 90 ? 'bg-red-500' : 
                      dailyPercentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(dailyPercentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs tc-text-tertiary mt-1">
                  {dailyPercentage.toFixed(1)}% of daily budget used
                </div>
              </div>

              {/* Monthly Spending */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm tc-text-secondary">Monthly Spending</span>
                  <span className="text-sm font-medium tc-text-primary">
                    ${costData.monthly?.spent?.toFixed(2) || '0.00'} / ${costData.monthly?.budget?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      monthlyPercentage > 90 ? 'bg-red-500' : 
                      monthlyPercentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(monthlyPercentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs tc-text-tertiary mt-1">
                  {monthlyPercentage.toFixed(1)}% of monthly budget used
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="tc-text-secondary">No cost data available</p>
            </div>
          )}
        </div>

        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Recent Costs</h3>
          {loading && !costData?.recentCosts ? (
            <div className="text-center py-4">
              <div className="tc-spinner mb-2"></div>
              <p className="tc-text-secondary text-sm">Loading recent costs...</p>
            </div>
          ) : costData?.recentCosts && costData.recentCosts.length > 0 ? (
            <div className="space-y-3">
              {costData.recentCosts.slice(0, 5).map((cost, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm tc-text-primary">
                        {format(new Date(cost.date), 'MMM dd, HH:mm')}
                      </div>
                      {cost.operation && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          cost.operation === 'relevance_check' 
                            ? 'bg-blue-100 text-blue-700' 
                            : cost.operation === 'article_analysis'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {cost.operation === 'relevance_check' ? 'Relevance' : 
                           cost.operation === 'article_analysis' ? 'Analysis' : 
                           cost.operation}
                        </span>
                      )}
                    </div>
                    {cost.description && (
                      <div className="text-xs tc-text-tertiary mt-0.5">
                        {cost.description}
                      </div>
                    )}
                    {cost.model && (
                      <div className="text-xs tc-text-tertiary">
                        Model: {cost.model}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium tc-text-primary">
                      ${cost.cost.toFixed(4)}
                    </div>
                    {cost.totalTokens && (
                      <div className="text-xs tc-text-tertiary">
                        {(cost.totalTokens / 1000).toFixed(1)}k tokens
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="tc-text-secondary">No recent costs available</p>
            </div>
          )}
        </div>
      </div>

      {/* Budget Alerts */}
      {costData && (dailyPercentage > 80 || monthlyPercentage > 80) && (
        <div className={`tc-card p-4 border-l-4 ${
          dailyPercentage > 90 || monthlyPercentage > 90 
            ? 'border-red-400 bg-red-50' 
            : 'border-yellow-400 bg-yellow-50'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className={`text-2xl ${
                dailyPercentage > 90 || monthlyPercentage > 90 
                  ? 'text-red-600' 
                  : 'text-yellow-600'
              }`}>
                ⚠️
              </span>
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                dailyPercentage > 90 || monthlyPercentage > 90 
                  ? 'text-red-800' 
                  : 'text-yellow-800'
              }`}>
                Budget Alert
              </p>
              <p className={`text-xs mt-1 ${
                dailyPercentage > 90 || monthlyPercentage > 90 
                  ? 'text-red-600' 
                  : 'text-yellow-600'
              }`}>
                {dailyPercentage > 90 && 'Daily budget nearly exhausted. '}
                {monthlyPercentage > 90 && 'Monthly budget nearly exhausted. '}
                Consider adjusting AI analysis frequency.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">Cost Breakdown</h3>
        {costData ? (
          <div className="space-y-6">
            {/* Budget Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold tc-text-primary">
                  ${costData.daily?.spent?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm tc-text-secondary">Today's Spending</div>
              </div>
              
              <div>
                <div className="text-2xl font-bold tc-text-primary">
                  ${costData.daily?.remaining?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm tc-text-secondary">Daily Remaining</div>
              </div>
              
              <div>
                <div className="text-2xl font-bold tc-text-primary">
                  ${costData.monthly?.spent?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm tc-text-secondary">This Month</div>
              </div>
              
              <div>
                <div className="text-2xl font-bold tc-text-primary">
                  ${costData.monthly?.remaining?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm tc-text-secondary">Monthly Remaining</div>
              </div>
            </div>

            {/* Operation Breakdown */}
            {costData.operationBreakdown && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium tc-text-primary mb-3">Today's Costs by Operation</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                    <div>
                      <div className="text-sm font-medium text-blue-900">Relevance Checks</div>
                      <div className="text-xs text-blue-700">Gemini Flash 2.5</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-blue-900">
                        ${costData.operationBreakdown?.relevance_check?.toFixed(4) || '0.0000'}
                      </div>
                      <div className="text-xs text-blue-700">
                        {costData.operationCounts?.relevance_check || 0} articles
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                    <div>
                      <div className="text-sm font-medium text-green-900">Deep Analysis</div>
                      <div className="text-xs text-green-700">Claude Sonnet 4</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-900">
                        ${costData.operationBreakdown?.article_analysis?.toFixed(4) || '0.0000'}
                      </div>
                      <div className="text-xs text-green-700">
                        {costData.operationCounts?.article_analysis || 0} articles
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="tc-text-secondary">No cost breakdown available</p>
          </div>
        )}
      </div>

      {/* Cost Information */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">AI Cost Information</h3>
        <div className="prose prose-sm tc-text-secondary">
          <h4 className="text-sm font-semibold tc-text-primary mb-2">Two-Stage Analysis Process</h4>
          <ul className="space-y-2 mb-4">
            <li><strong>Stage 1 - Relevance Check (Gemini Flash 2.5):</strong> $0.30 per 1M tokens</li>
            <li className="ml-4 text-xs">→ Filters out irrelevant articles (90%+ cost savings)</li>
            <li className="ml-4 text-xs">→ Checks if article relates to Estonia's threats</li>
            <li className="ml-4 text-xs">→ Runs on ALL articles from news sources</li>
            
            <li><strong>Stage 2 - Deep Analysis (Claude Sonnet 4):</strong> $3.00 per 1M tokens</li>
            <li className="ml-4 text-xs">→ Only analyzes relevant articles</li>
            <li className="ml-4 text-xs">→ Detailed threat assessment across all categories</li>
            <li className="ml-4 text-xs">→ Identifies threat actors and risk levels</li>
            
            <li><strong>Stage 3 - Critical Assessment (Claude Sonnet 4):</strong> $3.00 per 1M tokens</li>
            <li className="ml-4 text-xs">→ Extended analysis for high-risk clusters</li>
            <li className="ml-4 text-xs">→ Strategic assessment and predictions</li>
          </ul>
          
          <h4 className="text-sm font-semibold tc-text-primary mb-2">Cost Optimization</h4>
          <ul className="space-y-2">
            <li><strong>Before:</strong> ~$3/day per high-volume source (BBC, Reuters)</li>
            <li><strong>After:</strong> ~$0.30/day per source (90% reduction)</li>
            <li><strong>Budget Protection:</strong> Daily $10 limit, monthly $200 limit</li>
            <li><strong>Auto-pause:</strong> Analysis stops when budget exceeded</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
